/**
 * Run Modes Documentation
 *
 * How to execute strategies: backtest, optimization, WFA, and adaptive flow.
 */
import type { DocPage } from '../types';

export const runsDocs: DocPage[] = [
  {
    id: 'runs/backtest',
    title: 'Backtest',
    category: 'runs',
    tags: ['backtest', 'run', 'simulation', 'trades', 'equity', 'stats', 'single run'],
    version: '2.0',
    related: ['runs/optimization', 'runs/wfa', 'types/orders'],
    order: 1,
    description: 'Running a single backtest against historical candle data',
    content: `# Backtest

A backtest replays your strategy against historical candles to simulate how it would have traded. It is the fastest way to see whether a strategy idea has merit.

## How It Works

1. Candles are fetched for the chosen symbol, interval, and date range
2. Your strategy's \`define()\`, \`init()\`, and \`onBar()\` functions are called in sequence
3. The trading engine simulates order fills, portfolio tracking, and fee deduction
4. Results include every trade, performance stats, and an equity curve

## Inputs

| Setting | Description |
|---------|-------------|
| **Symbol** | Trading pair (e.g. BTCUSDT) |
| **Interval** | Candle timeframe (\`1h\`, \`4h\`, \`1d\`, etc.) |
| **Date range** | Start and end dates, or candle index range |
| **Strategy** | Your QSL strategy |
| **Parameters** | Values for each parameter defined in \`define()\` |
| **Allocation** | Starting capital (default 10,000 USDT) |
| **Trading mode** | LONG_ONLY, SHORT_ONLY, or BIDIRECTIONAL |
| **Fee %** | Per-trade fee (default 0.1%) |

## Trading Modes

| Mode | Description |
|------|-------------|
| **LONG_ONLY** | Only long (buy) positions. This is the default. |
| **SHORT_ONLY** | Only short (sell) positions. Automatically uses MIXED portfolio. |
| **BIDIRECTIONAL** | Both long and short positions allowed. |

## Output

A completed backtest returns:

- **Trades** — every entry and exit with prices, dates, P&L, and portfolio snapshots
- **Statistics** — trade count, win rate, total return, Sharpe ratio, max drawdown, profit factor, Calmar ratio
- **Signals** — the raw signal array from your strategy
- **Metadata** — symbol, interval, candle count, fee rate, and whether data was truncated

## Candle Limits

Very large date ranges are automatically capped to prevent excessive memory usage. If your range exceeds the system limit (~50,000 candles), only the most recent candles are used and a warning is included in the metadata.

## Tips

- Start with a single instrument and a wide date range to see overall behaviour
- Use the default parameters first, then move to Optimization to tune them
- Compare LONG_ONLY vs BIDIRECTIONAL to see if short signals add value
- Check the max drawdown — a high return with extreme drawdown is risky
`,
  },
  {
    id: 'runs/optimization',
    title: 'Optimization',
    category: 'runs',
    tags: ['optimization', 'grid search', 'parameter tuning', 'sharpe', 'target', 'combinations'],
    version: '2.0',
    related: ['runs/backtest', 'runs/wfa', 'params/optimization'],
    order: 2,
    description: 'Automatically finding the best parameter values via grid search',
    content: `# Optimization

Optimization tests every combination of parameter values to find the set that scores highest on a chosen metric. It runs a full backtest for each combination (grid search).

## How It Works

1. For each parameter marked \`optimize: true\`, a range of values is generated from its \`min\`, \`max\`, and \`step\`
2. The Cartesian product of all ranges produces the full grid of combinations
3. Each combination runs a complete backtest
4. Results are ranked by the optimization target metric
5. The best-scoring combination is returned along with all results

## Grid Size

The total number of combinations is the product of step counts:

\`\`\`
fastLength:  min=5,  max=25, step=5  → 5 values  (5, 10, 15, 20, 25)
slowLength:  min=20, max=60, step=10 → 5 values  (20, 30, 40, 50, 60)
rsiPeriod:   min=7,  max=21, step=7  → 3 values  (7, 14, 21)

Total combinations = 5 x 5 x 3 = 75 backtests
\`\`\`

> **Limit**: The maximum grid size is 50,000 combinations. If your ranges produce more, reduce the number of parameters or increase the step sizes.

## Optimization Targets

| Target | What It Maximises |
|--------|-------------------|
| **Sharpe Ratio** (default) | Risk-adjusted return |
| **Total Return** | Percentage return on capital |
| **Total Profit** | Absolute profit amount |
| **Win Rate** | Percentage of winning trades |
| **Profit Factor** | Gross profit / gross loss |
| **Calmar Ratio** | Return / max drawdown |
| **Max Drawdown** | Minimises maximum drawdown |
| **Composite** | Blended: 40% Sharpe + 30% win rate + 30% profit factor |
| **All** | Returns the best result for each major metric |

## Fixed Capital

Optimization always uses **fixed (non-compounding) capital** for each backtest. This prevents early-period results from snowballing via compound growth, which would bias the parameter search toward strategies that got lucky early.

## Execution Modes

| Mode | Behaviour |
|------|-----------|
| **Synchronous** | Blocks until complete — good for small grids |
| **Asynchronous** | Returns a job ID immediately; progress is streamed via SSE (Server-Sent Events) |

Large optimizations automatically run asynchronously. You can monitor progress in real time and the results are saved for 7 days.

## Tips

- Start with coarse steps (large step sizes) to explore broadly, then refine with finer steps around the best region
- Use Sharpe Ratio as the default target — it balances return and risk
- If you see the best result at a boundary (min or max), extend the range
- Watch out for overfitting: a perfect-looking result on one date range may not generalize. Use Walk-Forward Analysis to validate.
- Keep the grid under 5,000 combinations for fast iteration; save larger grids for final validation
`,
  },
  {
    id: 'runs/wfa',
    title: 'Walk-Forward Analysis',
    category: 'runs',
    tags: ['WFA', 'walk-forward', 'in-sample', 'out-of-sample', 'overfitting', 'robustness', 'validation', 'fold'],
    version: '2.0',
    related: ['runs/optimization', 'runs/backtest', 'params/optimization'],
    order: 3,
    description: 'Rolling-window validation to test strategy robustness and detect overfitting',
    content: `# Walk-Forward Analysis (WFA)

Walk-Forward Analysis is the gold standard for testing whether optimized parameters generalize to unseen data. It splits the date range into rolling windows and alternates between optimization and validation.

## Key Concepts

| Term | Meaning |
|------|---------|
| **In-Sample (IS)** | The training window — parameters are optimized here |
| **Out-of-Sample (OOS)** | The testing window — parameters are validated on unseen data |
| **Fold** | One IS + OOS pair |
| **Step Size** | How many candles the window advances between folds |
| **WFE** | Walk-Forward Efficiency — ratio of OOS to IS performance |

## How It Works

\`\`\`
              Fold 1                    Fold 2                    Fold 3
  ├────── IS ──────┤── OOS ──┤
              ├────── IS ──────┤── OOS ──┤
                          ├────── IS ──────┤── OOS ──┤
\`\`\`

1. The date range is divided into overlapping folds
2. For each fold:
   a. **Optimize** on the IS window (full grid search)
   b. **Backtest** the IS window with the best parameters → IS metrics
   c. **Backtest** the OOS window with the **same** parameters → OOS metrics
3. Compare IS vs OOS performance across all folds to measure robustness

## Configuration

| Setting | Description |
|---------|-------------|
| **In-Sample Period** | Length of the training window (days or candles) |
| **Out-of-Sample Period** | Length of the test window |
| **Step Size** | How far to advance between folds (defaults to OOS size) |
| **Parameter Ranges** | Same min/max/step as Optimization |
| **Optimization Target** | Metric used to select best parameters per fold |

## Parameter Policy

| Mode | Behaviour |
|------|-----------|
| **Optimize** (default) | Re-optimize parameters for each fold's IS window |
| **Fixed** | Use one set of parameters for all folds (tests robustness of a known configuration) |

## Results

Each fold reports IS and OOS metrics independently. The summary includes:

- **Average OOS return** and **Sharpe ratio** across all folds
- **Win rate** — fraction of folds with positive OOS return
- **Consistency** — how stable OOS returns are across folds (higher is better)
- **Robustness rating** — Excellent, Good, Fair, or Poor

### Robustness Rating

| Rating | Criteria |
|--------|----------|
| **Excellent** | OOS win rate >= 70% AND consistency >= 0.6 |
| **Good** | OOS win rate >= 60% AND consistency >= 0.5 |
| **Fair** | OOS win rate >= 50% AND consistency >= 0.4 |
| **Poor** | Below Fair thresholds |

## Overfitting Metrics

| Metric | What It Measures |
|--------|------------------|
| **Walk-Forward Efficiency (WFE)** | \`OOS annualised return / IS annualised return\`. Values near 1.0 mean the strategy performs similarly on unseen data. Values far below 1.0 suggest overfitting. |
| **Performance Drop** | Percentage of IS performance lost in OOS |
| **Sharpe Ratio Drop** | Difference between IS and OOS Sharpe ratios |
| **Win Rate Drop** | Difference between IS and OOS win rates |

## Tips

- A strategy rated **Good** or **Excellent** is far more trustworthy than one that only looks great in a single backtest
- If WFE is below 0.5, the optimized parameters are likely overfitting to historical noise
- Use IS percentages between 60–80% of the total window — too small won't optimize well, too large won't leave enough OOS data
- Run WFA on the same date range you optimized on to verify the optimization was not a fluke
- If results are **Poor**, simplify the strategy (fewer parameters, longer indicator periods) before tuning further
`,
  },
  {
    id: 'runs/adaptive-flow',
    title: 'Adaptive Flow',
    category: 'runs',
    tags: ['adaptive flow', 'live validation', 'real-time', 'health score', 'deviation', 're-optimization', 'monitoring', 'forward test'],
    version: '2.0',
    related: ['runs/wfa', 'runs/optimization'],
    order: 4,
    description: 'Live strategy validation and automatic re-optimization after WFA',
    content: `# Adaptive Flow

Adaptive Flow is the final stage of the strategy pipeline. After a strategy passes Walk-Forward Analysis, Adaptive Flow monitors its performance against live market data and automatically triggers re-optimization when the strategy's edge degrades.

## Pipeline Position

\`\`\`
Backtest → Optimization → WFA → Adaptive Flow
\`\`\`

You can only enter Adaptive Flow for a strategy that has completed WFA with acceptable results.

## How It Works

1. WFA results establish an **expected performance envelope** — statistical bands for win rate, Sharpe ratio, drawdown, and profit factor based on out-of-sample windows
2. You enter **Pre-Live** mode which shows readiness checks and expected performance bands
3. Starting live validation runs a fresh optimization using the optimal training window, then begins monitoring
4. Real-time price feeds drive strategy signal computation on each candle close
5. The system continuously compares actual vs expected metrics
6. If performance degrades beyond thresholds, re-optimization is proposed

## Key Metrics

| Metric | Description |
|--------|-------------|
| **Health Score** | 0–10 composite rating of forward robustness |
| **Confidence %** | Statistical maturity of the live validation data (increases as more candles are processed) |
| **Deviation Score** | How far live metrics have drifted from expected. WARNING at >= 0.60, DEGRADING at >= 0.80 |
| **Expected Envelope** | Win rate, Sharpe, drawdown, and profit factor ranges derived from WFA out-of-sample results |

## Runtime States

| State | Description |
|-------|-------------|
| **Pre-Live** | Strategy eligible but not yet monitoring. Shows readiness checks and expected bands. |
| **Collecting** | Just started, confidence below 25% |
| **Early Sample** | 25–70% confidence, partial data |
| **Active** | Normal monitoring (warmup complete) |
| **Warning** | Deviation score >= 0.60 — performance drifting |
| **Degrading** | Deviation score >= 0.80 — significant performance loss |
| **Re-optimizing** | Re-optimization is running |
| **Stabilized** | Post re-optimization, accelerated warmup |

## Expected vs Actual

The core of Adaptive Flow is comparing six metrics against their expected envelopes:

- **Win Rate** — percentage of winning trades
- **Sharpe Ratio** — risk-adjusted return
- **Trade Count** — number of trades generated
- **Average Return** — mean return per trade
- **Max Drawdown** — largest peak-to-trough decline
- **Profit Factor** — gross profit / gross loss

Each metric is shown with the expected range and the actual live value, making it easy to see where performance is holding and where it is diverging.

## Re-Optimization

When performance degrades beyond thresholds (or a configurable candle countdown is reached), the system proposes a re-optimization:

1. You receive a notification with the proposed re-optimization
2. You can **approve**, **reject**, or **defer** the proposal
3. If approved, a new optimization runs using the latest market data
4. New parameters replace the current ones and monitoring continues

Re-optimization uses the same parameter ranges and optimization target from your original WFA configuration.

## Multi-Asset (Pack) Mode

If your strategy was validated across multiple assets in WFA, Adaptive Flow can monitor all assets simultaneously:

- A **Pack Summary** view aggregates health across all assets
- You can switch to individual **Asset View** for per-symbol details
- Pack health uses median aggregation penalised by the weakest asset

## Tips

- Let confidence reach at least 70% before drawing conclusions about live performance
- A Health Score above 7 indicates the strategy is performing within expectations
- If deviation reaches WARNING, watch closely but do not intervene immediately — short-term variance is normal
- If deviation reaches DEGRADING, consider approving re-optimization promptly
- Compare the equity curve overlay (expected band vs actual line) for a visual sense of how the strategy is tracking
`,
  },
];
